import type React from "react";
import { useState } from "react";
import { TabSelectOption, TabSelectV2 } from "~/components/TabSelectV2";
import { cn } from "~/lib/utils";
import { NavAndIncomeChart } from "./NavAndIncomeChart";
import { ReturnVolatilityHistogramChart } from "~/modules/asset_screener/components/ReturnVolatilityHistogramChart";
import { usePerformanceChartData } from "./usePerformanceChartData";
import { HypotheticalPerformanceChart } from "./HypotheticalPerformanceChart";
import * as Analytics from "../../analytics";
import { SelectInput } from "~/components/SelectInput";
import type { ScreenerAsset } from "./types";

// // // //

enum PerformanceViewOptions {
    hypothetical = "hypothetical",
    navAndIncome = "nav_and_income",
    volatility = "volatility",
}

type PerformanceViewOption =
    (typeof PerformanceViewOptions)[keyof typeof PerformanceViewOptions];

/**
 * Sized to the hypothetical chart + summary table so no dead space sits above
 * the section below; the volatility tab runs slightly taller and may grow the
 * panel while selected.
 */
const PERFORMANCE_PANEL_MIN_HEIGHT_CLASS = "min-h-[400px]";

export function AssetResultRowCharts(props: { asset: ScreenerAsset }) {
    const { asset } = props;
    const assetID = asset.id;
    const assetName = asset.name;
    const [performanceView, setPerformanceView] = useState(
        PerformanceViewOptions.hypothetical,
    );
    const { chartData, isLoading } = usePerformanceChartData(assetID);
    const performancePoints = chartData?.points ?? [];

    const performanceViewOptions: TabSelectOption[] = [
        {
            label: "Hypothetical Growth of $10,000",
            value: PerformanceViewOptions.hypothetical,
        },
        {
            label: "NAV & Yield",
            value: PerformanceViewOptions.navAndIncome,
        },
        {
            label: "Total Return Volatility",
            value: PerformanceViewOptions.volatility,
        },
    ];

    return (
        <div>
            <p className="text-sm font-medium text-foreground">Performance</p>
            <SelectInput
                options={performanceViewOptions.map((each) => {
                    return {
                        value: each.value,
                        content: {
                            label: each.label,
                        },
                    };
                })}
                size="xs"
                placeholder="Performance Metric"
                placeholderPosition="inset"
                className="mt-3 xl:hidden"
                value={performanceView}
                onChange={(value: string) => {
                    Analytics.trackPerformanceChartChangeTab({
                        asset_id: assetID,
                        asset_name: assetName,
                        prev_value: performanceView,
                        next_value: value,
                    });
                    setPerformanceView(value as PerformanceViewOption);
                }}
            />
            <div className="mt-3 hidden xl:block">
                <TabSelectV2
                    options={performanceViewOptions}
                    value={performanceView}
                    onValueChange={(value) => {
                        Analytics.trackPerformanceChartChangeTab({
                            asset_id: assetID,
                            asset_name: assetName,
                            prev_value: performanceView,
                            next_value: value,
                        });
                        setPerformanceView(value as PerformanceViewOption);
                    }}
                />
            </div>

            <div className={cn("mt-3", PERFORMANCE_PANEL_MIN_HEIGHT_CLASS)}>
                {performanceView === PerformanceViewOptions.hypothetical && (
                    <HypotheticalPerformanceChart asset={asset} />
                )}

                {performanceView === PerformanceViewOptions.navAndIncome && (
                    <NavAndIncomeChart
                        assetID={assetID}
                        assetName={assetName}
                    />
                )}

                {performanceView === PerformanceViewOptions.volatility && (
                    <ReturnVolatilityHistogramChart
                        points={performancePoints}
                        isLoading={isLoading}
                        assetName={assetName}
                        showHeader={false}
                    />
                )}
            </div>
        </div>
    );
}
